import { useEffect, useRef, useState } from 'react';

/**
 * Tracks a derived value from an element's rendered width via
 * `ResizeObserver`. Drives container-query-style responsive layouts when
 * viewport-based media queries don't fit (e.g. inside a user-resizable
 * split panel where the parent's size is independent of the viewport).
 *
 * Only re-renders when the *derived* value changes — not on every pixel
 * tick from the observer. With a small enum (e.g. layout-mode), this
 * means at most a couple of re-renders per drag instead of dozens.
 */
export const useElementWidth = <T>(
  derive: (width: number) => T,
  initial: T
): {
  ref: React.RefObject<HTMLDivElement>;
  value: T;
} => {
  const ref = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState<T>(initial);
  // Keep the latest deriver in a ref so we don't tear down the observer
  // every time the consumer's deriver identity changes.
  const deriveRef = useRef(derive);
  deriveRef.current = derive;

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof ResizeObserver === 'undefined') {
      setValue(deriveRef.current(node.getBoundingClientRect().width));
      return;
    }
    const apply = (width: number) => {
      const next = deriveRef.current(width);
      setValue((prev) => (Object.is(prev, next) ? prev : next));
    };
    apply(node.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const w = entry.contentBoxSize?.[0]?.inlineSize ?? entry.contentRect.width ?? 0;
      apply(w);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return { ref, value };
};
